/* Images load on demand. Only the first mobile card warms one photo for its swipe demonstration, after page load. */
(() => {
  if (customElements.get('mr-card-gallery')) return;
  const desktop = matchMedia('(min-width: 700px) and (hover: hover) and (pointer: fine)');
  class CardGallery extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.link = this.querySelector('.product-card__media');
      this.inProductRow = !!this.closest('scroll-carousel, [data-mr-search]');
      this.toggleAttribute('data-in-product-row', this.inProductRow);
      this.images = JSON.parse(this.querySelector('[data-gallery-images]').textContent);
      this.cache = new Map();
      this.index = this.base = 0;
      this.request = 0;
      this.abort = new AbortController();
      const on = (node, event, handler, options = {}) => node.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      this.querySelectorAll('[data-gallery-step]').forEach(button => {
        button.hidden = false;
        on(button, 'click', () => this.step(Number(button.dataset.galleryStep)));
      });
      on(this.closest('product-card') || this, 'pointerenter', event => {
        if (desktop.matches && event.pointerType === 'mouse') this.show((this.base + 1) % this.images.length);
      });
      on(this.closest('product-card') || this, 'pointerleave', event => {
        if (desktop.matches && event.pointerType === 'mouse' && !this.contains(document.activeElement)) this.show(this.base);
      });
      on(this, 'focusout', () => {
        queueMicrotask(() => {
          if (desktop.matches && !this.contains(document.activeElement) && !this.matches(':hover')) this.show(this.base);
        });
      });
      on(this.link, 'pointerdown', event => {
        if (!event.isPrimary || event.pointerType === 'mouse' || this.inProductRow) return;
        this.dismissHint();
        // A new touch takes over the current visual position instead of snapping
        // back to the previous slide while its settling animation is running.
        const offset = this.slideAnimations?.length
          ? new DOMMatrixReadOnly(getComputedStyle(this.visibleImage()).transform).m41 : 0;
        this.cancelSlide();
        this.request++;
        this.index = this.displayedIndex || 0;
        this.suppressClickUntil = 0;
        this.gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, offset, horizontal: Math.abs(offset) > 1 };
        if (this.gesture.horizontal) this.paintDrag(this.gesture, offset);
      }, { passive: true });
      on(this.link, 'pointermove', event => {
        const gesture = this.gesture;
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = event.clientX - gesture.x + (gesture.offset || 0), dy = event.clientY - gesture.y;
        if (!gesture.horizontal && Math.abs(dy) > 12 && Math.abs(dy) >= Math.abs(dx)) {
          this.gesture = null;
          return;
        }
        if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy) * 1.3) {
          gesture.horizontal = true;
          if (!this.link.hasPointerCapture(event.pointerId)) this.link.setPointerCapture(event.pointerId);
        }
        if (gesture.horizontal) this.paintDrag(gesture, dx);
      }, { passive: true });
      on(this.link, 'pointerup', event => {
        const gesture = this.gesture;
        this.gesture = null;
        if (!gesture || event.pointerId !== gesture.id || !gesture.horizontal) return;
        this.suppressClickUntil = performance.now() + 500;
        const dx = event.clientX - gesture.x + (gesture.offset || 0);
        this.paintDrag(gesture, dx);
        if (Math.abs(dx) >= Math.min(55, this.link.clientWidth * .2)) {
          this.slideTo(gesture.target, gesture.direction, gesture.dx);
        } else {
          this.rebound(gesture);
        }
      });
      on(this.link, 'pointercancel', () => {
        if (this.gesture?.horizontal) this.rebound(this.gesture);
        this.gesture = null;
      });
      on(this.link, 'click', event => {
        if (performance.now() < (this.suppressClickUntil || 0)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true });
      on(this, 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      });
      this.show(0, false, false);
      this.setupHint();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.request++;
      this.cancelHint();
      this.cancelSlide();
      this.hintObserver?.disconnect();
      this.querySelectorAll('.mr-gallery__image').forEach(image => image.remove());
      this.removeAttribute('data-showing-extra');
    }
    step(direction) {
      this.closest('product-list, .product-list')?.querySelectorAll('mr-card-gallery[data-swipe-hint]').forEach(gallery => gallery.dismissHint());
      this.dismissHint();
      this.slideTo((this.index + direction + this.images.length) % this.images.length, direction);
    }
    visibleImage() {
      return this.displayedIndex ? this.cache.get(this.displayedIndex)?.image : this.link.querySelector('.product-card__image--primary');
    }
    imageAt(index) {
      return index ? this.loadImage(index) : { image: this.link.querySelector('.product-card__image--primary'), ready: Promise.resolve() };
    }
    paintDrag(gesture, dx) {
      const width = this.link.clientWidth;
      gesture.dx = Math.max(-width, Math.min(width, dx));
      const direction = dx < 0 ? 1 : -1;
      if (gesture.direction !== direction) {
        this.clearSlideStyles();
        gesture.direction = direction;
        gesture.target = ((this.displayedIndex || 0) + direction + this.images.length) % this.images.length;
        const entry = this.imageAt(gesture.target);
        gesture.next = entry.image;
        entry.ready.catch(() => {}); // A failed neighbour keeps the current photo available.
      }
      const current = this.visibleImage();
      this.slideImages = [current, gesture.next];
      this.classList.add('is-sliding');
      current.style.transform = `translate3d(${gesture.dx}px, 0, 0)`;
      gesture.next.style.transform = `translate3d(${gesture.dx + direction * width}px, 0, 0)`;
      gesture.next.style.opacity = '1';
    }
    async slideTo(target, direction, from = 0) {
      if (target == null) return;
      if (!from) this.cancelSlide();
      const token = this.slideToken = (this.slideToken || 0) + 1;
      this.request++;
      this.index = target;
      const current = this.visibleImage();
      const entry = this.imageAt(target);
      try { await entry.ready; } catch {
        if (token === this.slideToken) {
          this.cancelSlide();
          this.index = this.displayedIndex || 0;
        }
        return;
      }
      if (token !== this.slideToken || !this.isConnected) return;
      if (entry.image === current || matchMedia('(prefers-reduced-motion: reduce)').matches) {
        await this.show(target, true);
        return;
      }
      const width = this.link.clientWidth;
      this.slideImages = [current, entry.image];
      this.classList.add('is-sliding');
      const options = { duration: 280, easing: 'cubic-bezier(.22,.61,.36,1)', fill: 'forwards' };
      const frames = (start, end) => [{ transform: `translate3d(${start}px,0,0)`, opacity: 1 }, { transform: `translate3d(${end}px,0,0)`, opacity: 1 }];
      this.slideAnimations = [
        current.animate(frames(from, -direction * width), options),
        entry.image.animate(frames(from + direction * width, 0), options)
      ];
      await Promise.allSettled(this.slideAnimations.map(animation => animation.finished));
      if (token === this.slideToken) {
        // Commit the destination underneath the finished animation before removing
        // its compositor layer. Never reveal the previous photo between frames.
        this.commitImage(target, entry.image, true);
        this.cancelSlide();
      }
    }
    async rebound(gesture) {
      const token = this.slideToken = (this.slideToken || 0) + 1;
      const width = this.link.clientWidth;
      const options = { duration: matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220, easing: 'ease-out', fill: 'forwards' };
      this.slideAnimations = this.slideImages.map((image, i) => image.animate([
        { transform: `translate3d(${gesture.dx + (i ? gesture.direction * width : 0)}px,0,0)`, opacity: 1 },
        { transform: `translate3d(${i ? gesture.direction * width : 0}px,0,0)`, opacity: 1 }
      ], options));
      await Promise.allSettled(this.slideAnimations.map(animation => animation.finished));
      if (token === this.slideToken) this.cancelSlide();
    }
    clearSlideStyles() {
      this.slideImages?.forEach(image => {
        image.style.removeProperty('transform');
        image.style.removeProperty('opacity');
      });
      this.slideImages = [];
      this.classList.remove('is-sliding');
    }
    cancelSlide() {
      this.slideToken = (this.slideToken || 0) + 1;
      this.slideAnimations?.forEach(animation => animation.cancel());
      this.slideAnimations = [];
      this.clearSlideStyles();
    }
    loadImage(index, eager = true) {
      let entry = this.cache.get(index);
        if (!entry) {
          const data = this.images[index];
          const image = new Image(data.width, data.height);
          image.className = this.link.querySelector('.product-card__image--primary').className.replace('product-card__image--primary', 'mr-gallery__image');
          image.alt = data.alt;
          image.setAttribute('aria-hidden', 'true');
          image.draggable = false;
          image.decoding = 'async';
          image.loading = eager ? 'eager' : 'lazy';
          image.fetchPriority = 'low';
          image.sizes = this.link.querySelector('.product-card__image--primary').sizes;
          const ready = new Promise((resolve, reject) => {
            image.onload = () => image.decode().catch(() => {}).then(resolve);
            image.onerror = () => {
            image.remove();
            this.cache.delete(index);
            reject(new Error('Image unavailable'));
          };
          });
          entry = { image, ready };
          this.cache.set(index, entry);
          const widths = [200, 300, 400, 500, 600, 700, 800, 1000, 1200].filter(width => width < data.width);
          widths.push(Math.min(data.width, 1400));
          image.srcset = widths.map(width => {
            const url = new URL(data.src, location.href);
            url.searchParams.set('width', width);
            return `${url.href} ${width}w`;
          }).join(', ');
          image.src = data.src;
          this.link.append(image);
        }
        if (eager) entry.image.loading = 'eager';
        return entry;
    }
    async show(index, announce = false, eager = true) {
      if (this.hintAnimations?.length) this.cancelHint();
      this.cancelSlide();
      this.index = index;
      const request = ++this.request;
      let image;
      if (index > 0) {
        const entry = this.loadImage(index, eager);
        image = entry.image;
        try {
          await entry.ready;
        } catch {
          image.remove();
          this.cache.delete(index);
          if (request === this.request) {
            this.index = this.displayedIndex || 0;
            this.querySelector('[role="status"]').textContent = 'Image unavailable. Please try again.';
          }
          return;
        }
        if (request !== this.request || !this.isConnected) return;
      }
      this.commitImage(index, image, announce);
    }
    commitImage(index, image, announce = false) {
      this.index = index;
      this.cache.forEach(entry => {
        entry.image.classList.toggle('is-current', entry.image === image);
        entry.image.setAttribute('aria-hidden', String(entry.image !== image));
      });
      this.link.querySelector('.product-card__image--primary').setAttribute('aria-hidden', String(index > 0));
      this.toggleAttribute('data-showing-extra', index > 0);
      this.displayedIndex = index;
      if (announce) this.querySelector('[role="status"]').textContent = `Image ${index + 1} of ${this.images.length}`;
    }
    setupHint() {
      if (!this.hasAttribute('data-swipe-hint') || desktop.matches || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.hintKey = `mainrose-card-swipe-peek:${location.pathname}`;
      try { if (sessionStorage.getItem(this.hintKey)) return; } catch { /* Storage is optional. */ }
      this.hintObserver = new IntersectionObserver(entries => {
        this.hintVisible = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .7);
        clearTimeout(this.hintTimer);
        if (this.hintVisible) this.hintTimer = setTimeout(() => this.playHint(), 1200);
        else this.cancelHint();
      }, { threshold: .7 });
      this.hintObserver.observe(this);
    }
    async playHint() {
      if (!this.isConnected || !this.hintVisible || this.hintDone || this.gesture) return;
      const bounds = this.link.getBoundingClientRect();
      const foreground = document.elementFromPoint(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
      // Wait for initial loading and for cookie/newsletter dialogs to be dismissed.
      if (document.readyState !== 'complete' || document.hidden || !this.contains(foreground)) {
        this.hintTimer = setTimeout(() => this.playHint(), 600);
        return;
      }
      if (desktop.matches || matchMedia('(prefers-reduced-motion: reduce)').matches || navigator.connection?.saveData) return;
      const token = this.hintToken = (this.hintToken || 0) + 1;
      const request = this.request;
      const current = this.displayedIndex ? this.cache.get(this.displayedIndex)?.image : this.link.querySelector('.product-card__image--primary');
      if (!current?.complete || !current.naturalWidth) {
        this.hintTimer = setTimeout(() => this.playHint(), 600);
        return;
      }
      const nextIndex = ((this.displayedIndex || 0) + 1) % this.images.length;
      const entry = nextIndex ? this.loadImage(nextIndex) : null;
      const next = entry?.image || this.link.querySelector('.product-card__image--primary');
      try {
        if (entry) await entry.ready;
        await next.decode();
      } catch { return; }
      if (!this.isConnected || token !== this.hintToken || request !== this.request || !this.hintVisible || this.gesture) return;
      const options = { duration: 1000, iterations: 2, easing: 'ease-in-out' };
      const frames = (start, peek) => [
        { transform: `translateX(${start}%)`, opacity: 1, offset: 0 },
        { transform: `translateX(${peek}%)`, opacity: 1, offset: .35 },
        { transform: `translateX(${peek}%)`, opacity: 1, offset: .5 },
        { transform: `translateX(${start}%)`, opacity: 1, offset: .85 },
        { transform: `translateX(${start}%)`, opacity: 1, offset: 1 }
      ];
      this.dataset.hintState = 'playing';
      this.hintAnimations = [current.animate(frames(0, -45), options), next.animate(frames(100, 55), options)];
      this.hintDone = true;
      this.hintObserver?.disconnect();
      try { sessionStorage.setItem(this.hintKey, '1'); } catch { /* Storage is optional. */ }
      await Promise.allSettled(this.hintAnimations.map(animation => animation.finished));
      if (token === this.hintToken) { this.cancelHint(); this.dataset.hintState = 'done'; }
    }
    cancelHint() {
      this.hintToken = (this.hintToken || 0) + 1;
      clearTimeout(this.hintTimer);
      this.hintAnimations?.forEach(animation => animation.cancel());
      this.hintAnimations = [];
    }
    dismissHint() {
      this.hintDone = true;
      if (this.hintKey) {
        try { sessionStorage.setItem(this.hintKey, '1'); } catch { /* Storage is optional. */ }
      }
      this.cancelHint();
      this.hintObserver?.disconnect();
    }
  }
  customElements.define('mr-card-gallery', CardGallery);
})();
